import {Injectable} from '@angular/core';
import {BackendHttpService} from '../../core/backend-http.service';
import {UserCreditBilling} from '../../../models/Billing/user-credit-billing';
import {UserCredits} from '../../../models/Billing/user-credits';
import {ActivityPricing} from '../../../models/Billing/activity-pricing';
import {BillingDetails} from '../../../models/Billing/billing-details';
import {Invoice} from '../../../models/Billing/invoice';
import {Observable} from 'rxjs/Observable';
import {apiEndpoints, CommonConstant} from '../../app.constants';
import {DateUtils} from '../../utils/date.utils';
import {ErrorTemplate, ValidationError} from '../../../models/ErrorTemplate';

export enum ActivityPricingType {
  DISCOVER = 'discover',
  INFLUENCER = 'influencers',
  CAMPAIGN = 'campaigns',
  PROFILE = 'profile',
  REPORT_EXPORT = 'report export',
  MONITORING = 'monitoring'
}

@Injectable()
export class CreditBaseBillingService extends BackendHttpService<any> {
  private userCreditBilling: UserCreditBilling = new UserCreditBilling();

  get isLoadedUserCreditData() {
    return this.userCreditBilling.userCredit !== undefined;
  }

  get userCredit() {
    if (!this.userCreditBilling.userCredit) {
      return new UserCredits();
    }
    return this.userCreditBilling.userCredit;
  }

  get activityPricing() {
    if (!this.userCreditBilling.activityPricing) {
      return new ActivityPricing();
    }
    return this.userCreditBilling.activityPricing;
  }

  get listCreditUsage() {
    if (!this.userCreditBilling.listCreditUsage) {
      return [];
    }
    return this.userCreditBilling.listCreditUsage;
  }

  get billingDetails() {
    if (!this.userCreditBilling.billingDetails) {
      return new BillingDetails();
    }
    return this.userCreditBilling.billingDetails;
  }

  get listInvoice() {
    if (!this.userCreditBilling.listInvoice) {
      return [];
    }
    return this.userCreditBilling.listInvoice;
  }

  public loadUserCreditBilling(companyId) {
    return this.get(apiEndpoints.creditBilling.replace(':companyId', companyId)).subscribe((res) => {
      const creditBillingData = res.data;
      // get user credit
      const userCredit = new UserCredits({
          accountLevel: creditBillingData.accountLevel,
          pricePerCredit: creditBillingData.pricePerCredit,
          currency: creditBillingData.currency,
          availableCredits: creditBillingData.availableCredits,
          currentMonthUsedCredits: creditBillingData.currentMonthUsedCredits});

      this.userCreditBilling.userCredit = userCredit;

      // get activity pricing
      this.userCreditBilling.activityPricing = new ActivityPricing();
      creditBillingData.activityPricingList.forEach((activity) => this.classifyActivityPricing(activity));
      this.userCreditBilling.listCreditUsage = creditBillingData.dailyUsageList.map(usage => Object.assign({}, usage, {
        date: this.formatBillingDate(usage.date)
      } ));
      this.userCreditBilling.billingDetails = creditBillingData.billingDetails;
      this.userCreditBilling.listInvoice = [];
      creditBillingData.invoices.forEach((invoice, index) => {
        this.userCreditBilling.listInvoice.push(new Invoice(this.getInvoiceNumber(invoice.invoiceNumber, index),
          this.getInvoicePdfUrl(invoice.file, invoice.invoiceNumber, index), invoice.file.id, invoice.file.contentType,
          this.formatBillingDate(invoice.invoiceDate),
          this.formatBillingDate(invoice.dueDate), invoice.amount, invoice.currency, invoice.status));
      });
    });
  }

  private formatBillingDate(dateStr): string {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const dayMonth = DateUtils.formatDate(dateStr, {day: 'numeric', month: 'short'}, CommonConstant.US_LOCALE);
    return `${dayMonth}, ${year}`;
  }

  private getInvoicePdfUrl(invoiceFile, invoiceNumber, index) {
    if (!invoiceFile) {
      return null;
    }
    const formattedInvoiveNumber = this.getInvoiceNumber(invoiceNumber, index);
    return invoiceFile.diskFileName + `&response-content-disposition=attachment%3B%20filename=%22${formattedInvoiveNumber}.pdf%22`;
  }

  private getInvoiceNumber(invoiceNumber, index) {
    if (invoiceNumber) {
      return invoiceNumber;
    }
    // just a temporal solution, because, the invoice number is not defined yet on webservice
    // will be removed later
    return `INV_${index + 1}`;
  }

  private classifyActivityPricing(activityPricing) {
    const activityName = activityPricing.creditPackageActivity.name.toLowerCase();
    switch (activityName) {
      case ActivityPricingType.DISCOVER:
        this.userCreditBilling.activityPricing.discovery = activityPricing.numberOfCredits;
        break;
      case ActivityPricingType.INFLUENCER:
        this.userCreditBilling.activityPricing.influencers = activityPricing.numberOfCredits;
        break;
      case ActivityPricingType.CAMPAIGN:
        this.userCreditBilling.activityPricing.campaigns = activityPricing.numberOfCredits;
        break;
      case ActivityPricingType.PROFILE:
        this.userCreditBilling.activityPricing.profiles = activityPricing.numberOfCredits;
        break;
      case ActivityPricingType.REPORT_EXPORT:
        this.userCreditBilling.activityPricing.exportReport = activityPricing.numberOfCredits;
        break;
      case ActivityPricingType.MONITORING:
        this.userCreditBilling.activityPricing.monitoring = activityPricing.numberOfCredits;
        break;
    }
  }

  public saveOrUpdateCreditBilling(billingDetails: BillingDetails, companyId: number): Observable<UserCreditBilling> {
    const data = Object.assign({}, billingDetails, {
      companyId: companyId
    });
    return this.post(apiEndpoints.updateBillingDetails, data);
  }
}



// WEBPACK FOOTER //
// ./src/app/services/credit-based-billing/credit-base-billing.service.ts